import React from "react";
import { MdDownload } from "react-icons/md";
import axiosInstance from "config/axiosInstance";
import { format } from "date-fns";

interface ExportCSVProps {
    showClientHour: boolean;
    dateRange: { start: string, end: string };
}

// Helper function to convert seconds to days:hrs:min:sec
const convertSecondsToHMS = (totalSeconds: number) => {
    if (totalSeconds <= 0) return "0hrs 0min 0sec";

    const days = Math.floor(totalSeconds / (3600 * 24));
    const hours = Math.floor((totalSeconds % (3600 * 24)) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);

    let timeString = '';
    if (days > 0) timeString += `${days}d `;
    timeString += `${hours}hrs ${minutes}min ${seconds}sec`;

    return timeString;
};

const ExportCSV: React.FC<ExportCSVProps> = ({ showClientHour, dateRange }) => {
    const fetchTaskSessions = async (clientId: string) => {
        const taskSessionInitialResponse = await axiosInstance.get(`/v1/task_sessions/?client_delivery=${clientId}`);
        const taskSessionCount = taskSessionInitialResponse.data.count;

        if (taskSessionCount === 0) return [];

        const taskSessionFullResponse = await axiosInstance.get(`/v1/task_sessions/?client_delivery=${clientId}&page_size=${taskSessionCount}&start_time__gte=${dateRange.start}&start_time__lte=${dateRange.end}`);
        return taskSessionFullResponse.data.results;
    };

    const calculateDuration = (taskSessions: any[]) => {
        let totalDurationInSeconds = 0;
        taskSessions.forEach((taskSession: any) => {
            const taskStartTime = new Date(taskSession.start_time);
            const taskStopTime = taskSession.stop_time ? new Date(taskSession.stop_time) : null;

            if (taskStopTime && taskStopTime.getTime() > taskStartTime.getTime()) {
                totalDurationInSeconds += (taskStopTime.getTime() - taskStartTime.getTime()) / 1000;
            }
        });
        return totalDurationInSeconds;
    };

    const handleExportCSV = async () => {
        try {
            const csvData: string[][] = [];

            if (showClientHour) {
                const taskSessionResponse = await axiosInstance.get('/v1/task_sessions/');
                const taskSessions = await axiosInstance.get(`/v1/task_sessions/?page_size=${taskSessionResponse.data.count}&start_time__gte=${dateRange.start}&start_time__lte=${dateRange.end}`);
                const taskSessionResults = taskSessions.data.results;

                csvData.push(["Client ID", "Client Name", "Driver ID", "Date", "Task Name", "Started At", "Stopped At", "Total Duration"]);

                taskSessionResults.forEach((session: any) => {
                    const clientId = session.client_delivery_info?.id || "";
                    const clientName = session.client_delivery_info?.name || "";
                    const driverId = session.user?.id || "";
                    const taskName = session.task_info?.name || "";
                    const startTime = new Date(session.start_time);
                    const stopTime = session.stop_time ? new Date(session.stop_time) : null;
                    const startTimeFormatted = format(startTime, "HH:mm:ss");
                    const stopTimeFormatted = stopTime ? format(stopTime, "HH:mm:ss") : "";
                    const totalDuration = convertSecondsToHMS(stopTime ? (stopTime.getTime() - startTime.getTime()) / 1000 : 0);

                    csvData.push([clientId, clientName, driverId, format(startTime, "yyyy-MM-dd"), taskName, startTimeFormatted, stopTimeFormatted, totalDuration]);
                });
            } else {
                const clientDeliveryResponse = await axiosInstance.get('/v1/client_deliveries/');
                const clientDeliveries = await axiosInstance.get(`/v1/client_deliveries/?page_size=${clientDeliveryResponse.data.count}`);
                const clientDeliveryResults = clientDeliveries.data.results;

                csvData.push(["Client ID", "Client Name", "Task Count", "Started At", "Stopped At", "Total Duration"]);

                for (const client of clientDeliveryResults) {
                    const clientId = client.id;
                    const clientName = client.name;
                    const taskCount = client.tasks?.length || 0;

                    const taskSessions = await fetchTaskSessions(clientId);
                    let startAt = "";
                    let stopAt = "";
                    let totalDurationInSeconds = 0;

                    if (taskSessions.length > 0) {
                        const firstTask = taskSessions[0];
                        const lastTask = taskSessions[taskSessions.length - 1];
                        stopAt = firstTask.stop_time ? format(new Date(firstTask.stop_time), "yyyy-MM-dd") : "";
                        startAt = lastTask.start_time ? format(new Date(lastTask.start_time), "yyyy-MM-dd") : "";
                        totalDurationInSeconds = calculateDuration(taskSessions);
                    }

                    const totalDuration = convertSecondsToHMS(totalDurationInSeconds);
                    csvData.push([clientId, clientName, taskCount.toString(), startAt, stopAt, totalDuration]);
                }
            }

            const csvContent = "data:text/csv;charset=utf-8," + csvData.map(row => row.join(",")).join("\n");
            const encodedUri = encodeURI(csvContent);
            const link = document.createElement("a");
            link.setAttribute("href", encodedUri);
            link.setAttribute("download", "client_analysis.csv");
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error("Failed to export CSV", error);
        }
    };
    return (
        <div className="flex rounded-md bg-white text-primary/300 font-medium p-2 justify-center items-center gap-2 cursor-pointer" onClick={handleExportCSV}>
            <p className="text-subtitle-2">Export CSV</p>
            <MdDownload size={20} />
        </div>
    );
};

export default ExportCSV;